import { Phone, Compass, Heart, ArrowRight, Clock, Timer, HeartHandshake } from "lucide-react";

const primaryServices = [
  {
    id: "clarity_call",
    icon: Phone,
    title: "Clarity Call",
    duration: "15 minutes",
    price: "Free",
    priceNote: "Complimentary",
    desc: "Tell me what you're seeking. I'll be honest about whether I'm the right coach for you. No obligations, no pressure — just an honest 15 minutes on Zoom.",
    audience: "Open to all",
    cta: "Book free call",
    highlight: false,
  },
  {
    id: "happiness_coaching_6day",
    icon: Compass,
    title: "6-Day Happiness Coaching",
    duration: "6 days · daily on Zoom",
    price: "₹6,999",
    priceNote: "One-time",
    desc: "A guided six-day inner-shift programme. We meet, we reflect, you practice — and at the end, you carry a quietly different inner posture into the rest of your life.",
    audience: "Open to all · after a clarity call",
    cta: "Choose this path",
    highlight: true,
  },
  {
    id: "womens_listening",
    icon: Heart,
    title: "Women's Listening Sessions",
    duration: "45 minutes",
    price: "₹999",
    priceNote: "Per session",
    desc: "A warm, womanly space to put down what you've been carrying — relationships with partner, children, parents, colleagues, friends. I listen first. I share insights only if you want them.",
    audience: "For women only",
    cta: "Book a session",
    highlight: false,
  },
];

const focusedSessions = [
  {
    id: "time_management_60",
    icon: Timer,
    title: "Time, Priorities & Productivity",
    duration: "60 minutes",
    price: "₹1,999",
    priceNote: "Per session",
    desc: "A focused hour to untangle a crowded calendar. We look at what truly matters, gently release the rest, and leave you with a clear rhythm you can carry into your week.",
    audience: "Open to all",
    cta: "Book this session",
    highlight: false,
  },
  {
    id: "forgiveness_60",
    icon: HeartHandshake,
    title: "The Gift of Forgiveness",
    duration: "60 minutes",
    price: "₹1,999",
    priceNote: "Per session",
    desc: "A tender, private hour to lay down old hurts you may have been quietly carrying. Together we soften around what forgiveness really means — and gift yourself the lightness you deserve.",
    audience: "Open to all",
    cta: "Book this session",
    highlight: false,
  },
];

export default function Services() {
  return (
    <section id="services" className="relative py-20 lg:py-28 bg-sunlit-cream/60" data-testid="services-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-14">
          <p className="text-xs uppercase tracking-[0.2em] text-sunlit-mute mb-4">Ways we can work</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-sunlit-ink leading-tight">
            Soft doorways <br />
            into a <span className="italic text-sunlit-gold">lighter life</span>.
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6" data-testid="services-primary-row">
          {primaryServices.map((s) => (
            <ServiceCard key={s.id} s={s} />
          ))}
        </div>

        <div className="mt-16 mb-8 max-w-2xl">
          <p className="text-xs uppercase tracking-[0.2em] text-sunlit-mute mb-3">Focused sessions</p>
          <h3 className="font-serif-display text-3xl sm:text-4xl text-sunlit-ink leading-tight">
            One quiet hour, <span className="italic text-sunlit-gold">one clear shift</span>.
          </h3>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:max-w-4xl md:mx-auto" data-testid="services-focused-row">
          {focusedSessions.map((s) => (
            <ServiceCard key={s.id} s={s} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ s }) {
  const Icon = s.icon;
  const scrollToBook = (e) => {
    e.preventDefault();
    const el = document.querySelector("#book");
    if (el) {
      const evt = new CustomEvent("prefill-service", { detail: { service: s.id } });
      window.dispatchEvent(evt);
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <article
      className={`group relative rounded-3xl p-8 lg:p-9 transition-all hover:-translate-y-1 ${
        s.highlight
          ? "bg-sunlit-ink text-sunlit-ivory shadow-xl"
          : "bg-sunlit-ivory border border-stone-200 hover:shadow-lg"
      }`}
      data-testid={`service-card-${s.id}`}
    >
      {s.highlight && (
        <span className="absolute -top-3 left-8 inline-flex items-center rounded-full bg-sunlit-gold px-3 py-1 text-[10px] font-medium uppercase tracking-widest text-sunlit-ink">
          Most chosen
        </span>
      )}

      <div
        className={`h-12 w-12 rounded-2xl flex items-center justify-center mb-5 ${
          s.highlight ? "bg-sunlit-gold/20" : "bg-sunlit-gold/20"
        }`}
      >
        <Icon className={`h-5 w-5 ${s.highlight ? "text-sunlit-gold" : "text-sunlit-gold"}`} />
      </div>

      <h3 className={`font-serif-display text-2xl leading-tight ${s.highlight ? "text-sunlit-ivory" : "text-sunlit-ink"}`}>
        {s.title}
      </h3>

      <div className={`mt-2 flex items-center gap-1.5 text-xs ${s.highlight ? "text-sunlit-ivory/70" : "text-sunlit-mute"}`}>
        <Clock className="h-3.5 w-3.5" />
        <span>{s.duration}</span>
      </div>

      <div className="mt-5 flex items-baseline gap-2">
        <span className={`font-serif-display text-4xl ${s.highlight ? "text-sunlit-gold" : "text-sunlit-ink"}`}>
          {s.price}
        </span>
        <span className={`text-xs uppercase tracking-wider ${s.highlight ? "text-sunlit-ivory/60" : "text-sunlit-mute"}`}>
          {s.priceNote}
        </span>
      </div>

      <p className={`mt-5 text-sm leading-relaxed ${s.highlight ? "text-sunlit-ivory/80" : "text-sunlit-mute"}`}>
        {s.desc}
      </p>

      <p
        className={`mt-5 inline-flex items-center rounded-full px-3 py-1 text-[11px] uppercase tracking-wider ${
          s.highlight ? "bg-sunlit-ivory/10 text-sunlit-ivory/80" : "bg-sunlit-cream text-sunlit-mute"
        }`}
      >
        {s.audience}
      </p>

      <button
        onClick={scrollToBook}
        className={`mt-8 w-full inline-flex items-center justify-between rounded-full px-5 py-3 text-sm font-medium transition-all ${
          s.highlight
            ? "bg-sunlit-gold hover:bg-sunlit-gold-hover text-sunlit-ink"
            : "bg-sunlit-ink hover:bg-stone-700 text-sunlit-ivory"
        }`}
        data-testid={`service-cta-${s.id}`}
      >
        <span>{s.cta}</span>
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </button>
    </article>
  );
}
